import { ApiResult, HttpClient } from '../http.js';
import type { AnalysisDraftDto, AnalysisDraftUploadFile, AnalysisDraftUploadPlan, CreateAnalysisDraftRequest, UpdateAnalysisDraftRequest } from '../types/analysis-drafts.js';
import type { TenderCostEstimateDto } from '../types/tenders.js';

export class AnalysisDraftsApi {
  constructor(private readonly http: HttpClient) {}

  list(): Promise<ApiResult<AnalysisDraftDto[]>> {
    return this.http.get('/api/analysis-drafts');
  }

  create(body: CreateAnalysisDraftRequest): Promise<ApiResult<AnalysisDraftDto>> {
    return this.http.post('/api/analysis-drafts', { body });
  }

  get(id: string): Promise<ApiResult<AnalysisDraftDto>> {
    return this.http.get(this.path(id));
  }

  update(id: string, body: UpdateAnalysisDraftRequest): Promise<ApiResult<AnalysisDraftDto>> {
    return this.http.put(this.path(id), { body });
  }

  uploads(id: string, revision: number, files: AnalysisDraftUploadFile[]): Promise<ApiResult<AnalysisDraftUploadPlan>> {
    return this.http.post(`${this.path(id)}/uploads`, { body: { revision, files } });
  }

  reconcile(id: string): Promise<ApiResult<AnalysisDraftDto>> {
    return this.http.post(`${this.path(id)}/reconcile`);
  }

  removeFile(id: string, fileId: string, revision: number): Promise<ApiResult<AnalysisDraftDto>> {
    return this.http.delete(`${this.path(id)}/files/${encodeURIComponent(fileId)}`, { query: { revision } });
  }

  fileUrl(id: string, fileId: string): Promise<ApiResult<{ url: string }>> {
    return this.http.get(`${this.path(id)}/files/${encodeURIComponent(fileId)}/url`);
  }

  estimate(id: string, revision: number): Promise<ApiResult<TenderCostEstimateDto>> {
    return this.http.post(`${this.path(id)}/estimate`, { body: { revision } });
  }

  start(id: string, revision: number): Promise<ApiResult<{ id: string; engine: AnalysisDraftDto['engine'] }>> {
    return this.http.post(`${this.path(id)}/start`, { body: { revision } });
  }

  remove(id: string, revision: number): Promise<ApiResult<AnalysisDraftDto>> {
    return this.http.delete(this.path(id), { query: { revision } });
  }

  private path(id: string): string {
    return `/api/analysis-drafts/${encodeURIComponent(id)}`;
  }
}
